import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { companySettingsSchema, type CompanySettingsInput } from '@/schemas/settingsSchemas';
import type { CompanySettings } from '@/types/legacy';
import { Loader2, Building2 } from 'lucide-react';

export function CompanySettingsForm() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [settingsId, setSettingsId] = useState<string | null>(null);

  const form = useForm<CompanySettingsInput>({
    resolver: zodResolver(companySettingsSchema),
    defaultValues: {
      company_name: '',
      business_name: '',
      commercial: '',
      aux_title: '',
      address: '',
      phone: '',
      email: '',
      logo_url: '',
    },
  });

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      const { data, error } = await api
        .from('company_settings')
        .select('*')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setSettingsId(data.id);
        form.reset({
          company_name: data.company_name || '',
          business_name: data.business_name || '',
          commercial: data.commercial || '',
          aux_title: data.aux_title || '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          logo_url: data.logo_url || '',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudo cargar la configuración de la empresa',
        variant: 'destructive',
      });
    } finally {
      setFetching(false);
    }
  };

  const onSubmit = async (data: CompanySettingsInput) => {
    setLoading(true);
    try {
      const payload = {
        company_name: data.company_name,
        business_name: data.business_name || null,
        commercial: data.commercial || null,
        aux_title: data.aux_title || null,
        address: data.address || null,
        phone: data.phone || null,
        email: data.email || null,
        logo_url: data.logo_url || null,
      };

      if (settingsId) {
        const { error } = await api
          .from('company_settings')
          .update(payload)
          .eq('id', settingsId);

        if (error) throw error;
      } else {
        const { data: newSettings, error } = await api
          .from('company_settings')
          .insert([payload])
          .select()
          .single();

        if (error) throw error;
        if (newSettings) setSettingsId(newSettings.id);
      }

      toast({
        title: 'Configuración guardada',
        description: 'La configuración de la empresa se guardó correctamente',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar la configuración',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <Building2 className="w-6 h-6 text-primary" />
        <h3 className="text-xl font-semibold">Configuración de la Empresa</h3>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="company_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre de la Empresa *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Manufacturas Madelmold" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="business_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Razón Social</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Manufacturas Madelmold S.L." />
                  </FormControl>
                  <FormDescription>
                    Nombre legal registrado de la empresa
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="commercial"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre Comercial</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Madelmold" />
                  </FormControl>
                  <FormDescription>
                    Nombre comercial o marca
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="aux_title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título Auxiliar</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Advanced Composite Solutions" />
                  </FormControl>
                  <FormDescription>
                    Eslogan o título adicional
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Dirección</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Calle Principal 123, Ciudad, CP 12345" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Teléfono</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="+34 123 456 789" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" placeholder="info@magmacomposites.com" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="logo_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>URL del Logo</FormLabel>
                <FormControl>
                  <Input {...field} type="url" placeholder="https://ejemplo.com/logo.png" />
                </FormControl>
                <FormDescription>
                  URL pública de la imagen del logo de la empresa
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Button type="submit" disabled={loading} className="bg-gradient-magma hover:shadow-magma-hover">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar Configuración
            </Button>
          </div>
        </form>
      </Form>
    </Card>
  );
}
